import React, { useState, useEffect } from "react";
import {
  User,
  Search,
  ArrowLeft,
  Eye,
  MoreHorizontal,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  RefreshCw,
  CheckCircle,
  XCircle,
  Filter,
  AlertCircle,
  Loader,
  Download,
  FileText,
  Trash2,
  BanIcon,
} from "lucide-react";
import UserDetailView from "./UserDetailView";
import { adminUserService } from "@/services/api/admin.user.service";
import { adminReportService } from "@/services/api/admin.report.service";

const AdminUserDashboard = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserData, setSelectedUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showUserList, setShowUserList] = useState(true);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [filterStatus, setFilterStatus] = useState("all");
  const [totalPages, setTotalPages] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Report generation states
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportStartDate, setReportStartDate] = useState("");
  const [reportEndDate, setReportEndDate] = useState("");
  const [reportFormat, setReportFormat] = useState("PDF");
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [reportSuccess, setReportSuccess] = useState(null);
  const [reportId, setReportId] = useState(null);

  // Delete user states
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [userToSuspend, setUserToSuspend] = useState(null);
  const [suspendingUser, setSuspendingUser] = useState(false);
  const [suspendError, setSuspendError] = useState(null);
  const [suspendSuccess, setSuspendSuccess] = useState(null);
  // const [deleteInfo, setDeleteInfo] = useState(null);

  // Function to handle sorting
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

    // Helper function to determine if a user is new (added within last 7 days)

  const handleFormatChange = (e) => {
    setReportFormat(e.target.value);
    // Reset the report ID when format changes so user needs to generate a new report
    setReportId(null);
    setReportSuccess(null);
  };

  // Function to load users
  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await adminUserService.getUsers(page, limit, searchTerm);

      if (response.status === "success") {
        const transformedUsers = response.data.users.map((user) => ({
          id: user._id,
          name: user.fullName || "N/A",
          email: user.email || "N/A",
          phone: user.mobileNo || "N/A",
          address: user.address || "Not provided",
          city: user.city || "N/A",
          state: user.state || "N/A",
          pincode: user.pincode || "N/A",
          accountOpeningDate: user.createdAt || new Date().toISOString(),
          dematId: user.dematId || "N/A",
          clientId: user.clientId || "N/A",
          kycStatus: user.isKycVerified ? "Verified" : "Pending",
          status: user.status || "inactive",
          role: user.role || "User",
        }));

        setUsers(transformedUsers);
        // Update this line to use the pagination data from the response
        setTotalUsers(response.pagination.total || transformedUsers.length);
        // You might also want to store the total pages
        setTotalPages(
          response.pagination.pages ||
            Math.ceil(response.pagination.total / limit)
        );
      } else {
        setError("Failed to load users");
      }
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadUsers();
  }, [page, limit, sortBy, sortOrder, filterStatus]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page !== 1) setPage(1);
      else loadUsers();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Function to fetch detailed user data
  const fetchUserDetails = async (userId) => {
    setLoadingUserDetails(true);
    try {
      const userData = await adminUserService.getUserById(userId);

      if (userData.status === "success") {
        setSelectedUserData(userData);
      } else {
        setError("Failed to load user details");
      }
    } catch (err) {
      setError(err.message || "Error fetching user details");
    } finally {
      setLoadingUserDetails(false);
    }
  };

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setShowUserList(false);
    fetchUserDetails(user.id);
  };

  const handleBackToList = () => {
    setSelectedUser(null);
    setSelectedUserData(null);
    setShowUserList(true);
  };

  const handleRefresh = () => {
    setPage(1);
    setSearchTerm(""); // Clear any active search
    setFilterStatus("all"); // Reset filter
    loadUsers();
  };

  // Function to open report modal
  const openReportModal = () => {
    // Set default dates (last 30 days)
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);

    setReportStartDate(startDate.toISOString().split("T")[0]);
    setReportEndDate(endDate.toISOString().split("T")[0]);
    setReportFormat("PDF");
    setReportError(null);
    setReportSuccess(null);
    setShowReportModal(true);
  };


// Add this style at the top of your file (or in your CSS)
const styles = {
  newUserIndicator: {
    position: 'absolute',
    top: '-2px',
    right: '-2px',
    width: '8px',
    height: '8px',
    backgroundColor: '#3b82f6', // blue-500
    borderRadius: '50%',
    border: '2px solid white'
  }
};





// For 24 hours:
const isNewUser = (accountOpeningDate) => {
  const cutoffDate = new Date();
  cutoffDate.setHours(cutoffDate.getHours() - 24); 
  return new Date(accountOpeningDate) > cutoffDate;
};

  const closeReportModal = () => {
    setShowReportModal(false);
  };
  const handleStartDateChange = (e) => {
    setReportStartDate(e.target.value);
    setReportId(null);
    setReportSuccess(null);
  };

  const handleEndDateChange = (e) => {
    setReportEndDate(e.target.value);
    setReportId(null);
    setReportSuccess(null);
  };
  // Function to generate all users report
  const generateAllUsersReport = async () => {
    setGeneratingReport(true);
    setReportError(null);
    setReportSuccess(null);

    try {
      // Prepare filters based on current filterStatus
      const filters = {};
      if (
        filterStatus === "active" ||
        filterStatus === "inactive" ||
        filterStatus === "suspended"
      ) {
        filters.status = filterStatus;
      } else if (filterStatus === "verified" || filterStatus === "pending") {
        filters.verificationStatus = {
          kyc: filterStatus === "verified" ? "Verified" : "Pending",
        };
      }

      const response = await adminReportService.generateAllUsersReport(
        new Date(reportStartDate),
        new Date(reportEndDate),
        reportFormat,
        filters
      );

      if (response.success) {
        setReportSuccess(
          `Report generation initiated. Report ID: ${response.data.reportId}`
        );
        setReportId(response.data.reportId);
      } else {
        setReportError("Failed to generate report: " + response.message);
      }
    } catch (err) {
      setReportError(err.message || "Failed to generate report");
    } finally {
      setGeneratingReport(false);
    }
  };

  // Function to download the report
  const downloadReport = () => {
    if (reportId) {
      adminReportService.downloadReport(reportId);
    }
  };

  // Function to open delete user modal
  const openDeleteModal = (user) => {
    setUserToDelete(user);
    setDeleteError(null);
    setDeleteSuccess(null);
    setDeleteInfo(null);
    setShowDeleteModal(true);
  };

  // Function to close delete user modal
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
    setDeleteInfo(null);
  };
  const openSuspendModal = (user) => {
    setUserToSuspend(user);
    setSuspendError(null);
    setSuspendSuccess(null);
    setShowSuspendModal(true);
  };

  const closeSuspendModal = () => {
    setShowSuspendModal(false);
    setUserToSuspend(null);
  };
  const suspendUser = async () => {
    if (!userToSuspend) return;

    setSuspendingUser(true);
    setSuspendError(null);

    try {
      const response = await adminUserService.suspendUser(userToSuspend.id);

      if (response.status === "success") {
        setSuspendSuccess("User account suspended successfully");

        // Update user status in the list
        setUsers(
          users.map((user) =>
            user.id === userToSuspend.id
              ? { ...user, status: "suspended" }
              : user
          )
        );

        // Close modal after a short delay
        setTimeout(() => {
          closeSuspendModal();
        }, 2500);
      } else {
        setSuspendError(
          "Failed to suspend user: " + (response.message || "Unknown error")
        );
      }
    } catch (err) {
      setSuspendError(err.message || "Error suspending user");
    } finally {
      setSuspendingUser(false);
    }
  };

  // Update the modal component
  const SuspendUserModal = () => {
    if (!showSuspendModal || !userToSuspend) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
          <h2 className="text-xl font-semibold mb-2">Suspend User</h2>

          <div className="mb-6">
            <p className="text-gray-600">
              Are you sure you want to suspend the user{" "}
              <span className="font-semibold">{userToSuspend.name}</span>?
            </p>
            <p className="text-gray-500 text-sm mt-2">
              Suspended users will not be able to log in but their data will be
              preserved.
            </p>
          </div>

          {suspendError && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded border border-red-200">
              {suspendError}
            </div>
          )}

          {suspendSuccess && (
            <div className="mb-4 p-3 bg-green-50 text-green-600 rounded border border-green-200">
              {suspendSuccess}
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              onClick={closeSuspendModal}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              disabled={suspendingUser}
            >
              Cancel
            </button>

            <button
              onClick={suspendUser}
              disabled={suspendingUser || suspendSuccess}
              className={`px-4 py-2 rounded flex items-center ${
                suspendingUser || suspendSuccess
                  ? "bg-orange-400 cursor-not-allowed"
                  : "bg-orange-600 hover:bg-orange-700"
              } text-white`}
            >
              {suspendingUser ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <BanIcon className="h-4 w-4 mr-1" />
                  Suspend User
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };
  // Function to delete user
  // const deleteUser = async () => {
  //   if (!userToDelete) return;

  //   setDeletingUser(true);
  //   setDeleteError(null);
  //   setDeleteInfo(null);

  //   try {
  //     const response = await adminUserService.deleteUser(userToDelete.id);

  //     if (response.status === "success") {
  //       // Check if user was suspended instead of deleted
  //       if (response.message.includes("suspended")) {
  //         setDeleteSuccess("User account suspended successfully");
  //         setDeleteInfo({
  //           status: "suspended",
  //           hasHoldings: response.data?.hasHoldings,
  //           hasWalletMoney: response.data?.hasWalletMoney,
  //           walletBalance: response.data?.walletBalance || 0,
  //         });

  //         // Update user status in the list without reloading
  //         setUsers(
  //           users.map((user) =>
  //             user.id === userToDelete.id
  //               ? { ...user, status: "suspended" }
  //               : user
  //           )
  //         );
  //       } else {
  //         setDeleteSuccess("User successfully deleted");
  //         // Remove user from the list without reloading
  //         setUsers(users.filter((user) => user.id !== userToDelete.id));
  //         setTotalUsers(totalUsers - 1);
  //       }

  //       // Close modal after a short delay
  //       setTimeout(() => {
  //         closeDeleteModal();
  //       }, 2500);
  //     } else {
  //       setDeleteError(
  //         "Failed to delete user: " + (response.message || "Unknown error")
  //       );
  //     }
  //   } catch (err) {
  //     setDeleteError(err.message || "Error deleting user");
  //   } finally {
  //     setDeletingUser(false);
  //   }
  // };

  // Filter users by status if filterStatus is set
  const filteredUsers = users
    .filter((user) => {
      if (filterStatus === "all") return true;
      if (filterStatus === "active") return user.status === "active";
      if (filterStatus === "inactive") return user.status === "inactive";
      if (filterStatus === "suspended") return user.status === "suspended";
      if (filterStatus === "verified") return user.kycStatus === "Verified";
      if (filterStatus === "pending") return user.kycStatus === "Pending";
      return true;
    })
    .filter((user) => {
      if (!startDate && !endDate) return true;

      const userDate = new Date(user.accountOpeningDate);
      const start = startDate ? new Date(startDate) : null;
      // Set end to the end of the day (23:59:59.999)
      const end = endDate
        ? new Date(new Date(endDate).setHours(23, 59, 59, 999))
        : null;

      if (start && !end) {
        return userDate >= start;
      }
      if (!start && end) {
        return userDate <= end;
      }
      if (start && end) {
        return userDate >= start && userDate <= end;
      }
      return true;
    });

  // Status badge component
  const StatusBadge = ({ status }) => {
    if (status === "active") {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
          Active
        </span>
      );
    } else if (status === "suspended") {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
          Suspended
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
          Inactive
        </span>
      );
    }
  };

  // KYC badge component
  const KycBadge = ({ status }) => {
    const isVerified = status === "Verified";
    return (
      <span
        className={`px-2 py-1 text-xs font-medium rounded-full ${
          isVerified
            ? "bg-blue-100 text-blue-800"
            : "bg-yellow-100 text-yellow-800"
        }`}
      >
        {status}
      </span>
    );
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortBy !== field)
      return <ChevronDown className="h-4 w-4 text-gray-400" />;
    return sortOrder === "asc" ? (
      <ChevronUp className="h-4 w-4 text-blue-600" />
    ) : (
      <ChevronDown className="h-4 w-4 text-blue-600" />
    );
  };

  // Report Modal Component
  const ReportModal = () => {
    if (!showReportModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
          <h2 className="text-xl font-semibold mb-4">
            Generate All Users Report
          </h2>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              className="w-full p-2 border border-gray-300 rounded"
              value={reportStartDate}
              onChange={handleStartDateChange}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              className="w-full p-2 border border-gray-300 rounded"
              value={reportEndDate}
              onChange={handleEndDateChange}
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Format
            </label>
            <select
              className="w-full p-2 border border-gray-300 rounded"
              value={reportFormat}
              onChange={handleFormatChange}
            >
              <option value="PDF">PDF</option>
              <option value="EXCEL">Excel</option>
            </select>
          </div>

          {reportError && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded border border-red-200">
              {reportError}
            </div>
          )}

          {reportSuccess && (
            <div className="mb-4 p-3 bg-green-50 text-green-600 rounded border border-green-200">
              {reportSuccess}
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              onClick={closeReportModal}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
            >
              Close
            </button>

            {reportId ? (
              <button
                onClick={downloadReport}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
              >
                <Download className="h-4 w-4 mr-1" />
                Download
              </button>
            ) : (
              <button
                onClick={generateAllUsersReport}
                disabled={
                  generatingReport || !reportStartDate || !reportEndDate
                }
                className={`px-4 py-2 rounded flex items-center ${
                  generatingReport || !reportStartDate || !reportEndDate
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                } text-white`}
              >
                {generatingReport ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-1" />
                    Generate Report
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Delete User Modal
  // const DeleteUserModal = () => {
  //   if (!showDeleteModal || !userToDelete) return null;

  //   return (
  //     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
  //       <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
  //         <h2 className="text-xl font-semibold mb-2">Delete User</h2>

  //         <div className="mb-6">
  //           <p className="text-gray-600">
  //             Are you sure you want to delete the user{" "}
  //             <span className="font-semibold">{userToDelete.name}</span>?
  //           </p>
  //           <p className="text-gray-500 text-sm mt-2">
  //             This action cannot be undone. All data associated with this user
  //             will be permanently removed.
  //           </p>
  //           <p className="text-gray-500 text-sm mt-2">
  //             Note: If the user has active holdings or wallet balance, their
  //             account will be suspended instead of deleted.
  //           </p>
  //         </div>

  //         {deleteError && (
  //           <div className="mb-4 p-3 bg-red-50 text-red-600 rounded border border-red-200">
  //             {deleteError}
  //           </div>
  //         )}

  //         {deleteSuccess && (
  //           <div className="mb-4 p-3 bg-green-50 text-green-600 rounded border border-green-200">
  //             {deleteSuccess}
  //           </div>
  //         )}

  //         {deleteInfo && (
  //           <div className="mb-4 p-3 bg-blue-50 text-blue-600 rounded border border-blue-200">
  //             <p className="font-medium">Account suspension reason:</p>
  //             <ul className="text-sm mt-1 list-disc pl-5">
  //               {deleteInfo.hasHoldings && <li>User has active holdings</li>}
  //               {deleteInfo.hasWalletMoney && (
  //                 <li>
  //                   User has a wallet balance of{" "}
  //                   {deleteInfo.walletBalance.toFixed(2)}
  //                 </li>
  //               )}
  //             </ul>
  //           </div>
  //         )}

  //         <div className="flex justify-end space-x-3">
  //           <button
  //             onClick={closeDeleteModal}
  //             className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
  //             disabled={deletingUser}
  //           >
  //             Cancel
  //           </button>

  //           <button
  //             onClick={deleteUser}
  //             disabled={deletingUser || deleteSuccess}
  //             className={`px-4 py-2 rounded flex items-center ${
  //               deletingUser || deleteSuccess
  //                 ? "bg-red-400 cursor-not-allowed"
  //                 : "bg-red-600 hover:bg-red-700"
  //             } text-white`}
  //           >
  //             {deletingUser ? (
  //               <>
  //                 <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
  //                 Processing...
  //               </>
  //             ) : (
  //               <>
  //                 <Trash2 className="h-4 w-4 mr-1" />
  //                 Delete User
  //               </>
  //             )}
  //           </button>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // };

  // Loading state
  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin text-indigo-500 mb-4" size={36} />
      </div>
    );
  }

  // Error state
  if (error && users.length === 0) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-600">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          <span>{error}</span>
        </div>
        <button
          onClick={handleRefresh}
          className="mt-3 flex items-center text-sm text-red-700 hover:text-red-800"
        >
          <RefreshCw className="h-4 w-4 mr-1" /> Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="">
      {/* Report Modal */}
      <ReportModal />

      {/* Delete User Modal */}
      {/* <DeleteUserModal /> */}
      <SuspendUserModal />

      {/* Show either User List or User Details based on state */}
      {showUserList ? (
        <>
          {/* Dashboard Header */}
          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-xl font-semibold text-gray-800">
                  User Management
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  {totalUsers} total users • Page {page} of {totalPages || 1}
                </p>
              </div>

              <div className="flex items-center mt-4 md:mt-0 space-x-2">
                <button
                  onClick={openReportModal}
                  className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                  title="Generate Report"
                >
                  <FileText size={16} className="mr-1" />
                  Generate Report
                </button>
                <button
                  onClick={handleRefresh}
                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Refresh"
                >
                  <RefreshCw size={18} />
                </button>
              </div>
            </div>

            {/* Search and Filter Row */}
            <div className="mt-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <div className="flex-1">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search users by name, email, or client ID..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Search
                    className="absolute left-3 top-2.5 text-gray-400"
                    size={18}
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                  <select
                    className="pl-3 pr-8 py-2 border border-gray-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                    <option value="verified">KYC Verified</option>
                    <option value="pending">KYC Pending</option>
                  </select>
                  <Filter
                    className="absolute right-2 top-2.5 text-gray-400 pointer-events-none"
                    size={16}
                  />
                </div>
                {/* --- Add these two blocks for date filtering --- */}
                <div>
                  <input
                    type="date"
                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={endDate || new Date().toISOString().split("T")[0]} // Block future dates
                    placeholder="Start date"
                  />
                </div>
                <div>
                  <input
                    type="date"
                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate || undefined}
                    max={new Date().toISOString().split("T")[0]} // Block future dates
                    placeholder="End date"
                  />
                </div>
                {/* Optional: Clear button */}
                {(startDate || endDate) && (
                  <button
                    className="ml-2 text-xs text-blue-600 underline"
                    onClick={() => {
                      setStartDate("");
                      setEndDate("");
                    }}
                    type="button"
                  >
                    Clear Dates
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* User Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort("fullName")}
                  >
                    <div className="flex items-center">
                      User
                      <SortIcon field="fullName" />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Role
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Contact
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort("createdAt")}
                  >
                    <div className="flex items-center">
                      Joined
                      <SortIcon field="createdAt" />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    KYC
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                     <td className="px-6 py-4 whitespace-nowrap">
  <div className="flex items-center">
    <div className="relative">
      <div className="h-10 w-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-medium">
        {user.name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .substring(0, 2)
          .toUpperCase()}
      </div>
      {/* Add the blue dot indicator for new users */}
      {isNewUser(user.accountOpeningDate) && (
        <span style={styles.newUserIndicator} />
      )}
    </div>
    <div className="ml-4">
      <div className="text-sm font-medium text-gray-900">
        {user.name}
      </div>
      <div className="text-sm text-gray-500">
        {user.email}
      </div>
      {user.clientId !== "N/A" && (
        <div className="text-xs text-gray-400">
          ID: {user.clientId}
        </div>
      )}
    </div>
  </div>
</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-700">
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {user.phone}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {new Date(
                            user.accountOpeningDate
                          ).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={user.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <KycBadge status={user.kycStatus} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-3">
                          <button
                            onClick={() => handleViewDetails(user)}
                            className="text-blue-600 hover:text-blue-900 flex items-center"
                          >
                            <Eye size={16} className="mr-1" />
                            View
                          </button>
                          <button
                            onClick={() => openSuspendModal(user)}
                            className="text-orange-600 hover:text-orange-800 flex items-center"
                            disabled={user.status === "suspended"}
                          >
                            <BanIcon size={16} className="mr-1" />
                            {user.status === "suspended"
                              ? "Suspended"
                              : "Suspend"}
                          </button>
                          {/* {user.status === "suspended" && (
                            <button
                              onClick={() => openDeleteModal(user)}
                              className="text-red-600 hover:text-red-900 flex items-center"
                            >
                              <Trash2 size={16} className="mr-1" />
                              suspended
                            </button>
                          )} */}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-6 py-12 text-center text-gray-500"
                    >
                      {searchTerm ? (
                        <div>
                          <div className="flex justify-center mb-3">
                            <Search className="h-10 w-10 text-gray-300" />
                          </div>
                          <p>No users match your search criteria</p>
                        </div>
                      ) : (
                        <div>
                          <div className="flex justify-center mb-3">
                            <User className="h-10 w-10 text-gray-300" />
                          </div>
                          <p>No users found</p>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between">
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{" "}
                    <span className="font-medium">
                      {(page - 1) * limit + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-medium">
                      {Math.min(page * limit, totalUsers)}
                    </span>{" "}
                    of <span className="font-medium">{totalUsers}</span> results
                  </p>
                </div>
                <div>
                  <nav
                    className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                    aria-label="Pagination"
                  >
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                        page === 1
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-500 hover:bg-gray-50"
                      }`}
                    >
                      Previous
                    </button>

                    {/* Page Numbers */}
                    {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                      // Logic to show pages around current page
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = idx + 1;
                      } else if (page <= 3) {
                        pageNum = idx + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + idx;
                      } else {
                        pageNum = page - 2 + idx;
                      }

                      if (pageNum <= totalPages) {
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border ${
                              page === pageNum
                                ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                                : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                            } text-sm font-medium`}
                          >
                            {pageNum}
                          </button>
                        );
                      }
                      return null;
                    })}

                    <button
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page === totalPages}
                      className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                        page === totalPages
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-500 hover:bg-gray-50"
                      }`}
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      ) : // User details view with loading state
      loadingUserDetails ? (
        <div className="flex items-center justify-center h-64">
          <Loader className="animate-spin text-indigo-500 mb-4" size={36} />
          <span className="ml-3 text-gray-600">Loading user details...</span>
        </div>
      ) : selectedUserData ? (
        <div>
          <div className="border-b border-gray-200 px-6 py-4">
        
        
          </div>
          <UserDetailView
            userData={selectedUserData}
            onBack={handleBackToList}
          />
        </div>
      ) : (
        <div className="p-8 text-center">
          <div className="text-red-500 mb-4">
            <XCircle size={48} className="mx-auto" />
          </div>
          <h2 className="text-xl font-semibold mb-2">
            Failed to load user details
          </h2>
          <p className="text-gray-600 mb-4">
            We couldn't retrieve the details for this user.
          </p>
      
      
        </div>
      )}
    </div>
  );
};

export default AdminUserDashboard;
